import type React from "react"
import { GBPNavBadge } from "@/features/google-business/components/GBPNavBadge"
import {
  Users,
  ShoppingCart,
  Settings,
  LayoutDashboard,
  Package,
  Warehouse,
  type LucideIcon,
  Layers,
  Tag,
  DollarSign,
  ListTree,
  ClipboardList,
  FileText,
  ArrowRightLeft,
  RefreshCw,
  Download,
  Upload,
  UsersRound,
  Gift,
  Award,
  FileCheck,
  Quote,
  Truck,
  RotateCcw,
  ShoppingBag,
  Building2,
  Store,
  MapPin,
  Scale,
  Boxes,
  Factory,
  Shuffle,
  Palette,
  SlidersHorizontal,
  PackageCheck,
  Route,
  Hash,
  Layers3,
  Link2,
  BarChart3,
  CalendarClock,
  TrendingUp,
  ClipboardCheck,
  PackageSearch,
  WarehouseIcon,
  Zap,
  Contact,
  MapPinned,
  Star,
  FileSpreadsheet,
  CreditCard,
  Handshake,
  UserCheck,
  Target,
  PieChart,
  ShoppingBasket,
  PackageOpen,
  Percent,
  Ticket,
  Printer,
  Mail,
  Calculator,
  ScrollText,
  BadgePercent,
  Ship,
  Workflow,
  MessageCircle,
  Megaphone,
  LayoutGrid,
  Monitor,
  FilePlus2,
  LifeBuoy,
} from "lucide-react";
import { appsNavigationConfig } from "./navigationAppsConfig";

// Helper to build list route for a doctype
// Uses the generic /list/:doctype pattern for unmapped doctypes
function listRoute(doctype: string): string {
  return `/list/${encodeURIComponent(doctype)}`;
}

// Helper for Single DocTypes (Stock Settings, Selling Settings, etc.)
// Single DocTypes have exactly one record whose name equals the doctype name
function singleDocRoute(doctype: string): string {
  const encoded = encodeURIComponent(doctype);
  return `/doc/${encoded}/${encoded}`;
}

// Sub-menu item within a group
export interface SubMenuItem {
  name: string;
  href: string;
  icon: LucideIcon;
  Badge?: React.ComponentType;
  adminOnly?: boolean;
}

// Group of sub-menu items with a title
export interface SubMenuGroup {
  title: string;
  items: SubMenuItem[];
}

// First-level navigation item
export interface NavigationItem {
  name: string;
  railLabel?: string; // Short label shown ONLY in the app rail; `name` (full) stays in the module panel + rail hover tooltip
  href?: string; // Direct link (if no subMenu)
  icon: LucideIcon;
  color: string;
  accentColor?: string; // Tailwind color token for active tints (e.g., "cyan", "teal")
  subMenu?: SubMenuGroup[]; // If present, clicking opens second level
  requiresProvisioning?: boolean; // If true, shows reduced opacity when not provisioned
  requiresLineOA?: boolean; // If true, only show when LINE Messaging API is connected
  appAccessKey?: string; // Maps to CP app_access key for sidebar gating (member-level)
  appRegistryKey?: string; // Maps to App Registry name for tenant-level install check
  installGatedEverywhere?: boolean; // Opt-in: ALSO hide from the menu grid + dashboard "Your apps" when appRegistryKey is not installed (rail/drawer always check it). Plan 1966 Sheets.
  requiresExogroTenant?: boolean; // Plan 1934: pilot-gate — only show for the Exogro tenant (tenantCompany includes 574sn1poep). Remove when System Health generalizes (F9).
}

// Divider item for visual separation
export interface NavigationDivider {
  type: "divider";
  label?: string; // Optional label above divider (e.g., "Apps")
}

// Combined type for navigation config
export type NavigationConfigItem = NavigationItem | NavigationDivider;

// Type guard for divider
export function isDivider(item: NavigationConfigItem): item is NavigationDivider {
  return "type" in item && item.type === "divider";
}

// Helper to get only navigation items (excludes dividers)
export function getNavigationItems(): NavigationItem[] {
  return navigationConfig.filter((item): item is NavigationItem => !isDivider(item));
}

export const navigationConfig: NavigationConfigItem[] = [
  // ============================================
  // DASHBOARD - Direct Link
  // ============================================
  {
    name: "แดชบอร์ด",
    href: "/",
    icon: LayoutDashboard,
    color: "bg-gradient-to-br from-blue-500 to-blue-600",
    accentColor: "blue",
  },

  // ============================================
  // SUPPORT — customer help desk
  // ============================================
  {
    name: "ศูนย์ช่วยเหลือ",
    href: "/support",
    icon: LifeBuoy,
    color: "bg-gradient-to-br from-sky-500 to-blue-600",
    accentColor: "sky",
  },

  // ============================================
  // BUSINESS DASHBOARD — configurable analytics (Plan 1921)
  // ============================================
  {
    name: "แดชบอร์ดธุรกิจ",
    href: "/dashboard/business",
    icon: BarChart3,
    color: "bg-gradient-to-br from-emerald-500 to-emerald-600",
    accentColor: "emerald",
    requiresProvisioning: true,
  },
  // ============================================
  // MARKETING — Meta Ads (Plan 1955); Google/TikTok Ads join here later
  // ============================================
  {
    name: "การตลาด",
    icon: Megaphone,
    color: "bg-gradient-to-br from-blue-500 to-blue-600",
    accentColor: "blue",
    requiresProvisioning: true,
    subMenu: [
      {
        title: "การตลาด",
        items: [
          { name: "Meta Ads", href: "/marketing/meta", icon: Megaphone },
        ],
      },
    ],
  },

  // ============================================
  // SYSTEM HEALTH — tenant infra monitoring (Plan 1934)
  // ============================================
  {
    name: "สุขภาพระบบ",
    href: "/system-health",
    icon: Monitor,
    color: "bg-gradient-to-br from-sky-500 to-sky-600",
    accentColor: "sky",
    requiresProvisioning: true,
    requiresExogroTenant: true, // Plan 1934 pilot — Exogro only until F9 generalizes
  },

  // ============================================
  // PRODUCTS
  // ============================================
  {
    name: "สินค้า",
    icon: Package,
    color: "bg-gradient-to-br from-cyan-500 to-cyan-600",
    accentColor: "cyan",
    requiresProvisioning: true,
    appAccessKey: "products",
    subMenu: [
      {
        title: "แคตตาล็อก",
        items: [
          { name: "รายการสินค้า", href: "/item", icon: Package }, // Mapped route
          { name: "กลุ่มสินค้า", href: listRoute("Item Group"), icon: Layers },
          { name: "แบรนด์", href: listRoute("Brand"), icon: Tag },
          { name: "สินค้าทดแทน", href: listRoute("Item Alternative"), icon: Shuffle },
          { name: "ชุดสินค้า", href: listRoute("Product Bundle"), icon: Boxes },
          { name: "ผู้ผลิตสินค้า", href: listRoute("Item Manufacturer"), icon: Factory },
        ],
      },
      {
        title: "ราคา",
        items: [
          { name: "ราคาสินค้า", href: "/inventory/item-price", icon: DollarSign }, // Mapped route
          { name: "กฎการตั้งราคา", href: "/inventory/pricing-rule", icon: ListTree }, // Mapped route
          { name: "รายการราคา", href: listRoute("Price List"), icon: ClipboardList },
          { name: "กฎการจัดส่ง", href: listRoute("Shipping Rule"), icon: Ship },
          { name: "คูปองส่วนลด", href: listRoute("Coupon Code"), icon: Ticket },
        ],
      },
      {
        title: "คุณสมบัติ",
        items: [
          { name: "คุณสมบัติสินค้า", href: listRoute("Item Attribute"), icon: Palette },
          { name: "แม่แบบปรับแต่ง", href: listRoute("Item Customization Template"), icon: SlidersHorizontal },
        ],
      },
    ],
  },

  // ============================================
  // INVENTORY
  // ============================================
  {
    name: "คลังสินค้า",
    icon: Warehouse,
    color: "bg-gradient-to-br from-teal-500 to-teal-600",
    accentColor: "teal",
    requiresProvisioning: true,
    appAccessKey: "products",
    subMenu: [
      {
        title: "คลังสินค้า",
        items: [
          { name: "คลังสินค้า", href: listRoute("Warehouse"), icon: Warehouse },
          { name: "ประเภทคลังสินค้า", href: listRoute("Warehouse Type"), icon: WarehouseIcon },
        ],
      },
      {
        title: "ธุรกรรม",
        items: [
          { name: "รายการเคลื่อนไหวสต็อก", href: "/stock/stock-entry", icon: ArrowRightLeft }, // Mapped route
          { name: "ปรับยอดสต็อก", href: "/stock/stock-reconciliation", icon: RefreshCw }, // Mapped route
          { name: "ใบขอวัสดุ", href: listRoute("Material Request"), icon: Boxes },
          { name: "รายการหยิบสินค้า", href: listRoute("Pick List"), icon: ClipboardCheck },
          { name: "เที่ยวจัดส่ง", href: listRoute("Delivery Trip"), icon: Route },
        ],
      },
      {
        title: "การรับสินค้า",
        items: [
          { name: "ใบรับสินค้า", href: listRoute("Purchase Receipt"), icon: PackageCheck },
          { name: "กฎการจัดวาง", href: listRoute("Putaway Rule"), icon: PackageSearch },
        ],
      },
      {
        title: "ซีเรียลและล็อต",
        items: [
          { name: "หมายเลขซีเรียล", href: listRoute("Serial No"), icon: Hash },
          { name: "ล็อตสินค้า", href: listRoute("Batch"), icon: Layers3 },
          { name: "ชุดซีเรียลและล็อต", href: listRoute("Serial and Batch Bundle"), icon: Link2 },
        ],
      },
      {
        title: "รายงาน",
        items: [
          { name: "บัญชีสต็อก", href: listRoute("Stock Ledger Entry"), icon: FileText },
          { name: "ยอดคงเหลือสต็อก", href: listRoute("Bin"), icon: Scale },
          { name: "สต็อกคาดการณ์", href: listRoute("Bin"), icon: TrendingUp },
          { name: "อายุสต็อก", href: listRoute("Bin"), icon: CalendarClock },
          { name: "สต็อกตามคลัง", href: listRoute("Bin"), icon: BarChart3 },
        ],
      },
      {
        title: "เครื่องมือ",
        items: [
          { name: "นำเข้าสต็อก", href: listRoute("Data Import"), icon: Upload },
          { name: "ส่งออกสต็อก", href: listRoute("Data Export"), icon: Download },
          { name: "ลงรายการมูลค่าใหม่", href: listRoute("Repost Item Valuation"), icon: RefreshCw },
          { name: "ตรวจยอดสต็อกด่วน", href: listRoute("Bin"), icon: Zap },
        ],
      },
    ],
  },

  // ============================================
  // CUSTOMERS
  // ============================================
  {
    name: "ลูกค้า",
    icon: Users,
    color: "bg-gradient-to-br from-green-500 to-green-600",
    accentColor: "green",
    requiresProvisioning: true,
    appAccessKey: "customers",
    subMenu: [
      {
        title: "ลูกค้า",
        items: [
          { name: "ลูกค้าทั้งหมด", href: "/customers", icon: Users }, // Mapped route
          { name: "กลุ่มลูกค้า", href: listRoute("Customer Group"), icon: UsersRound },
        ],
      },
      {
        title: "ผู้ติดต่อ",
        items: [
          { name: "ผู้ติดต่อ", href: listRoute("Contact"), icon: Contact },
          { name: "ที่อยู่", href: listRoute("Address"), icon: MapPinned },
        ],
      },
      {
        title: "สะสมคะแนน",
        items: [
          { name: "คะแนนสะสม", href: listRoute("Loyalty Point Entry"), icon: Gift },
          { name: "โปรแกรมสะสมคะแนน", href: listRoute("Loyalty Program"), icon: Award },
          { name: "รายการคะแนน", href: listRoute("Loyalty Point Entry"), icon: Star },
        ],
      },
    ],
  },

  // ============================================
  // ORDERS (SELLING)
  // ============================================
  {
    name: "คำสั่งซื้อ",
    icon: ShoppingCart,
    color: "bg-gradient-to-br from-purple-500 to-purple-600",
    accentColor: "purple",
    requiresProvisioning: true,
    appAccessKey: "orders",
    subMenu: [
      {
        title: "การขาย",
        items: [
          { name: "คำสั่งซื้อ", href: "/orders", icon: ShoppingCart }, // Mapped route
          { name: "ใบเสนอราคา", href: listRoute("Quotation"), icon: Quote },
          { name: "คำสั่งซื้อแบบเหมา", href: listRoute("Blanket Order"), icon: ScrollText },
        ],
      },
      {
        title: "ใบแจ้งหนี้",
        items: [
          { name: "ใบแจ้งหนี้ขาย", href: "/selling/sales-invoice", icon: FileCheck }, // Mapped route
          { name: "ใบแจ้งหนี้ POS", href: listRoute("POS Invoice"), icon: CreditCard },
        ],
      },
      {
        title: "การจัดส่ง",
        items: [
          { name: "ใบส่งสินค้า", href: listRoute("Delivery Note"), icon: Truck },
          { name: "การคืนสินค้า", href: listRoute("Sales Invoice"), icon: RotateCcw }, // Returns are filtered Sales Invoices
          { name: "ใบติดตั้ง", href: listRoute("Installation Note"), icon: Workflow },
        ],
      },
      {
        title: "ทีมขาย",
        items: [
          { name: "พาร์ทเนอร์ขาย", href: listRoute("Sales Partner"), icon: Handshake },
          { name: "พนักงานขาย", href: listRoute("Sales Person"), icon: UserCheck },
          { name: "เป้าหมายขาย", href: listRoute("Target Detail"), icon: Target },
        ],
      },
      {
        title: "รายงาน",
        items: [
          { name: "วิเคราะห์ยอดขาย", href: listRoute("Sales Order"), icon: PieChart },
          { name: "ยอดขายตามสินค้า", href: listRoute("Sales Order Item"), icon: ShoppingBasket },
          { name: "ยอดขายตามลูกค้า", href: listRoute("Sales Order"), icon: Users },
          { name: "สินค้ารอจัดส่ง", href: listRoute("Sales Order Item"), icon: PackageOpen },
        ],
      },
    ],
  },

  // ============================================
  // SALES CHANNELS
  // ============================================
  {
    name: "ช่องทางขาย",
    icon: Store,
    color: "bg-gradient-to-br from-indigo-500 to-indigo-600",
    accentColor: "indigo",
    requiresProvisioning: true,
    appAccessKey: "channels",
    subMenu: [
      {
        title: "ช่องทางขาย",
        items: [
          { name: "ภาพรวม", href: "/channels", icon: Store },
          { name: "TikTok Shop", href: listRoute("TikTok Listing"), icon: ShoppingBag },
          { name: "Lazada", href: listRoute("Lazada Listing"), icon: ShoppingBag },
        ],
      },
      {
        title: "สถานที่",
        items: [
          { name: "Google Business", href: "/google-business", icon: MapPin, Badge: GBPNavBadge },
        ],
      },
    ],
  },

  // ============================================
  // LINE OA (conditional — requires LINE Messaging API connection)
  // ============================================
  {
    name: "LINE OA",
    icon: MessageCircle,
    color: "bg-gradient-to-br from-green-500 to-green-600",
    accentColor: "green",
    requiresProvisioning: true,
    requiresLineOA: true,
    subMenu: [
      {
        title: "LINE OA",
        items: [
          { name: "ภาพรวม", href: "/line-oa", icon: LayoutDashboard },
          { name: "ริชเมนู", href: "/line-oa/rich-menus", icon: LayoutGrid },
          { name: "ส่งข้อความ", href: "/line-oa/broadcast", icon: Megaphone },
          { name: "แคมเปญ", href: "/line-oa/campaigns", icon: Target },
          { name: "ผู้ติดตาม", href: "/line-oa/audience", icon: Users },
        ],
      },
    ],
  },

  // ============================================
  // POINT REWARDS
  // ============================================
  {
    name: "คะแนนสะสม",
    icon: Gift,
    color: "bg-gradient-to-br from-orange-500 to-orange-600",
    accentColor: "orange",
    requiresProvisioning: true,
    subMenu: [
      {
        title: "สะสมคะแนน",
        items: [
          { name: "คะแนนสะสม", href: listRoute("Loyalty Point Entry"), icon: Gift },
          { name: "โปรแกรมสะสมคะแนน", href: listRoute("Loyalty Program"), icon: Award },
          { name: "รายการคะแนน", href: listRoute("Loyalty Point Entry"), icon: Star },
        ],
      },
    ],
  },

  // ============================================
  // BOOKING
  // ============================================
  {
    name: "การจอง",
    icon: CalendarClock,
    color: "bg-gradient-to-br from-rose-500 to-rose-600",
    accentColor: "rose",
    requiresProvisioning: true,
    appAccessKey: "products",
    subMenu: [
      {
        title: "การจอง",
        items: [
          { name: "รายการจอง", href: listRoute("Booking"), icon: CalendarClock },
          { name: "ตั้งค่าการจอง", href: listRoute("Booking Resource Config"), icon: SlidersHorizontal },
        ],
      },
    ],
  },

  // ============================================
  // SETTINGS
  // ============================================
  {
    name: "ตั้งค่า",
    icon: Settings,
    color: "bg-gradient-to-br from-gray-500 to-gray-600",
    accentColor: "gray",
    subMenu: [
      {
        title: "ทั่วไป",
        items: [
          { name: "ตั้งค่าบริษัท", href: listRoute("Company"), icon: Building2 },
          { name: "ทีม", href: "/settings/team", icon: UsersRound, adminOnly: true },
        ],
      },
      {
        title: "ตั้งค่าสต็อก",
        items: [
          { name: "ตั้งค่าสต็อก", href: singleDocRoute("Stock Settings"), icon: Warehouse },
          { name: "หน่วยวัด", href: listRoute("UOM"), icon: Scale },
          { name: "ตัวแปลงหน่วยวัด", href: listRoute("UOM Conversion Factor"), icon: Calculator },
        ],
      },
      {
        title: "ตั้งค่าการขาย",
        items: [
          { name: "ตั้งค่าการขาย", href: singleDocRoute("Selling Settings"), icon: Store },
          { name: "พื้นที่ขาย", href: listRoute("Territory"), icon: MapPin },
          { name: "ภาษีและค่าใช้จ่ายขาย", href: listRoute("Sales Taxes and Charges Template"), icon: BadgePercent },
        ],
      },
      {
        title: "ตั้งค่าการซื้อ",
        items: [
          { name: "ตั้งค่าการซื้อ", href: singleDocRoute("Buying Settings"), icon: ShoppingBag },
          { name: "ภาษีและค่าใช้จ่ายซื้อ", href: listRoute("Purchase Taxes and Charges Template"), icon: Percent },
        ],
      },
      {
        title: "พิมพ์และอีเมล",
        items: [
          { name: "สร้างเอกสาร", href: "/print-studio/create", icon: FilePlus2 },
          { name: "รูปแบบการพิมพ์", href: "/settings/print-formats", icon: Printer },
          { name: "ตั้งค่าการพิมพ์", href: singleDocRoute("Print Settings"), icon: FileSpreadsheet },
          { name: "เทมเพลตอีเมล", href: listRoute("Email Template"), icon: Mail },
        ],
      },
    ],
  },

  // Apps section (divider + embedded apps) lives in navigationAppsConfig.ts
  ...appsNavigationConfig,
];

// Helper to find the active app-rail item that has a subMenu for the given path
export function findActiveAppWithSubMenu(pathname: string): NavigationItem | undefined {
  let afterDivider = false;
  for (const item of navigationConfig) {
    if (isDivider(item) && item.label === "Apps") {
      afterDivider = true;
      continue;
    }
    if (afterDivider && !isDivider(item) && item.subMenu && item.subMenu.length > 0) {
      // Check direct href match
      if (item.href && (pathname === item.href || pathname.startsWith(item.href + "/"))) {
        return item;
      }
      // Check sub-menu item match
      const found = item.subMenu.some((group) =>
        group.items.some(
          (subItem) => pathname === subItem.href || pathname.startsWith(subItem.href + "/")
        )
      );
      if (found) return item;
    }
  }
  return undefined;
}

// Helper to get app items (items after the "Apps" divider, excluding Home/All Apps)
export function getAppItems(): NavigationItem[] {
  let afterDivider = false;
  const apps: NavigationItem[] = [];
  for (const item of navigationConfig) {
    if (isDivider(item) && item.label === "Apps") {
      afterDivider = true;
      continue;
    }
    if (afterDivider && !isDivider(item)) {
      // Skip the "Home" and "All Apps" entries — dashboard is already home
      if (item.href === "/" || item.href === "/all-apps") continue;
      apps.push(item);
    }
  }
  return apps;
}

// Helper to get ERP modules (items before the "Apps" divider)
export function getErpModules(): NavigationItem[] {
  const modules: NavigationItem[] = [];
  for (const item of navigationConfig) {
    if (isDivider(item) && item.label === "Apps") break;
    if (!isDivider(item)) modules.push(item);
  }
  return modules;
}

// Helper to get the primary href for a module
// Direct href if available, otherwise first sub-item's href
export function getModulePrimaryHref(item: NavigationItem): string {
  if (item.href) return item.href;
  if (item.subMenu && item.subMenu.length > 0 && item.subMenu[0].items.length > 0) {
    return item.subMenu[0].items[0].href;
  }
  return "/";
}

// Helper to check if a navigation item has a submenu
export function hasSubMenu(item: NavigationItem): item is NavigationItem & { subMenu: SubMenuGroup[] } {
  return item.subMenu !== undefined && item.subMenu.length > 0;
}

// Helper to find the active sub-item within ERP modules for a given path
export function findActiveSubItem(pathname: string): SubMenuItem | undefined {
  const erpModules = getErpModules();
  for (const item of erpModules) {
    if (!item.subMenu) continue;
    for (const group of item.subMenu) {
      for (const subItem of group.items) {
        if (pathname === subItem.href || pathname.startsWith(subItem.href + "/")) {
          return subItem;
        }
      }
    }
  }
  return undefined;
}

// Helper to find which first-level item contains a given path
export function findParentNavItem(pathname: string): NavigationItem | undefined {
  for (const item of navigationConfig) {
    // Skip dividers
    if (isDivider(item)) continue;

    // Check direct href
    if (item.href && (pathname === item.href || pathname.startsWith(item.href + "/"))) {
      return item;
    }
    // Check submenu items
    if (item.subMenu) {
      const found = item.subMenu.some((group) =>
        group.items.some(
          (subItem) => pathname === subItem.href || pathname.startsWith(subItem.href + "/")
        )
      );
      if (found) return item;
    }
  }
  return undefined;
}
